/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

/**
 * Lightweight registry that lets the global keyboard shortcut layer
 * ask the currently-mounted LyricsView to jump to the next section.
 *
 * "Section" here means a paragraph-boundary in the lyric list — the
 * next line whose start time is significantly further out than the
 * cadence of the surrounding lines, which is how lyric providers
 * mark a verse / chorus break. The LyricsView owns the actual
 * DOM-scroll implementation; this module just carries the imperative
 * jump function so the shortcut binding doesn't need to reach into
 * the view.
 */

type NextSectionFn = () => boolean

// Read/written imperatively only (no reactive subscribers), so a plain
// mutable ref — not an observable — is the honest signal here.
let currentHandler: NextSectionFn | null = null

/**
 * Registered by the LyricsView on connect; the returned function
 * clears the registration on disconnect. Only one view can register
 * at a time — the most-recent registration wins so a lyrics view
 * remounting mid-page always drives the shortcut.
 */
export function registerLyricsSectionHandler(handler: NextSectionFn): () => void {
    currentHandler = handler
    return () => {
        if (currentHandler === handler) currentHandler = null
    }
}

/**
 * Invoke the registered handler, if any. Returns true when the
 * handler ran and advanced (so the shortcut can preventDefault),
 * false otherwise.
 */
export function advanceLyricsSection(): boolean {
    if (!currentHandler) return false
    return currentHandler()
}
